import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import type { DnsZoneDto, DnsZonesResponse } from '@jordan/types';
import { Alert, Breadcrumb, Button, DataTable, type DataTableColumn, PageHeader, Spinner } from '@jordan/ui';
import { AppChrome } from '../../components/AppChrome';
import { apiGet } from '../../lib/api';
import { cloudSidebar } from './cloudSidebar';

const PROVIDER = {
  gcp: { label: 'Google Cloud', variant: 'primary' as const },
  aws: { label: 'AWS', variant: 'warning' as const },
};

export function CloudDnsPage() {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const [data, setData] = useState<DnsZonesResponse | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const load = () => {
    setLoading(true);
    setErr(null);
    apiGet<DnsZonesResponse>('/api/cloud/dns/zones')
      .then(setData)
      .catch(() => setErr('Could not load domains.'))
      .finally(() => setLoading(false));
  };
  useEffect(() => {
    load();
  }, []);

  const open = (z: DnsZoneDto) => navigate(`/cloud/dns/${z.provider}/${encodeURIComponent(z.id)}`);

  const columns: DataTableColumn<DnsZoneDto>[] = [
    { key: 'name', header: 'Domain', sortable: true, render: (z) => <span className="fw-semibold">{z.name}</span> },
    {
      key: 'provider',
      header: 'Served through',
      sortable: true,
      sortValue: (z) => PROVIDER[z.provider].label,
      render: (z) => {
        const p = PROVIDER[z.provider];
        return <span className={`badge bg-${p.variant} bg-opacity-25 text-${p.variant}`}>{p.label}</span>;
      },
    },
    { key: 'recordCount', header: 'Records', align: 'end', sortable: true, sortValue: (z) => z.recordCount ?? -1, render: (z) => (z.recordCount == null ? '—' : z.recordCount) },
    {
      key: 'actions',
      header: '',
      align: 'end',
      render: (z) => (
        <Button variant="light" size="sm" icon="ph-arrow-right" onClick={() => open(z)}>
          Manage
        </Button>
      ),
    },
  ];

  return (
    <AppChrome secondarySidebar={cloudSidebar(navigate, pathname)}>
      <PageHeader
        title={
          <>
            Cloud Management - <span className="fw-normal">Cloud DNS</span>
          </>
        }
        breadcrumb={<Breadcrumb items={[{ icon: 'ph-house', href: '/' }, { label: 'Cloud Management' }, { label: 'Cloud DNS' }]} />}
        actions={
          <Button variant="light" icon="ph-arrows-clockwise" disabled={loading} onClick={load}>
            Refresh
          </Button>
        }
      />

      <div className="content">
        {err && (
          <Alert variant="warning" icon="ph-warning" dismissible>
            {err}
          </Alert>
        )}
        {data?.providers.gcp.error && (
          <Alert variant="warning" icon="ph-warning">
            Google Cloud DNS: {data.providers.gcp.error}
          </Alert>
        )}
        {data?.providers.aws.error && (
          <Alert variant="warning" icon="ph-warning">
            AWS Route 53: {data.providers.aws.error}
          </Alert>
        )}
        {data && !data.providers.aws.configured && (
          <Alert variant="info" icon="ph-info">
            AWS isn’t configured — add AWS credentials (with Route 53 access) to include Route 53 domains.
          </Alert>
        )}

        {data === null && loading ? (
          <Spinner />
        ) : data && data.zones.length === 0 ? (
          <div className="card border-0 shadow-sm">
            <div className="card-body text-center text-muted py-5">No domains found across the connected providers.</div>
          </div>
        ) : data ? (
          <div className="card border-0 shadow-sm">
            <DataTable
              columns={columns}
              rows={data.zones}
              getRowKey={(z) => `${z.provider}:${z.id}`}
              searchable
              searchPlaceholder="Search domains…"
              initialSort={{ key: 'name', dir: 'asc' }}
              onRowClick={open}
            />
          </div>
        ) : null}
      </div>
    </AppChrome>
  );
}
